// TipoRiesgoPage.tsx
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table";
import { useMemo, useState } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import {
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import {
  useFetchGetTiposRiesgo,
  useFetchDeleteTipoRiesgo,
  useFetchPatchEstadoTipoRiesgo,
} from "@/shared/actions/controlcambios";
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  useTableFilter,
} from "@/shared";
import { useUiConfirmModalStore } from "@/store/ui";
import { TipoRiesgo } from "../../types";
import { toFilterParams } from "../../utils";
import { useAuthStore } from "@/store/auth/auth.store";
import { useUserModulesStore } from "@/store/auth";
import { SystemModulesEnum } from "@/auth/interfaces";

export default function TipoRiesgoPage() {
  const user = useAuthStore((s) => s.user) as any;
  const userModules = useUserModulesStore((s) => s.modules);
  const isSuper = !!user?.is_superuser || !!user?.is_staff;
  const tieneAcceso = userModules.includes(SystemModulesEnum.CONTROL_CAMBIOS_TIPO_RIESGO);

  const returnUrl = ROUTER_PATHS.control_cambios.tipoRiesgoNav;
  const navigate = useNavigate();
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore((s) => s.setConfirmDialogIsOpen);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  const { data: response, isLoading, isRefetching } = useFetchGetTiposRiesgo({
    page: pageIndex,
    size: pageSize,
    ...toFilterParams(columnFilters),
  });

  const patchEstado = useFetchPatchEstadoTipoRiesgo();
  const deleteTipoRiesgo = useFetchDeleteTipoRiesgo();

  const onEdit = (original: TipoRiesgo) => {
    setConfirmDialog({
      isOpen: true,
      title: "Editar tipo de riesgo",
      subtitle: "¿Está seguro que desea editar este registro?",
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrl}/editar/${original.tipoRiesgoId}`);
      },
    });
  };

  const onDelete = (origin: TipoRiesgo) => {
    setConfirmDialog({
      isOpen: true,
      title: "Eliminar tipo de riesgo",
      subtitle: `¿Está seguro que desea eliminar el tipo de riesgo "${origin.nombre}"? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        deleteTipoRiesgo.mutate(origin.tipoRiesgoId!);
      },
    });
  };

  const columns = useMemo<MRT_ColumnDef<TipoRiesgo>[]>(
    () => [
      {
        accessorKey: "nombre",
        header: "Nombre",
        size: 220,
        Cell: ({ row }) => row.original?.nombre || "N/A",
      },
      {
        accessorKey: "estado",
        header: "¿Está activo?",
        size: 140,
        filterVariant: "select",
        filterSelectOptions: [
          { text: "Activo", value: "true" },
          { text: "Inactivo", value: "false" },
        ],
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={!!row.original.estado}
            titulo={row.original.estado ? "Activo" : "Inactivo"}
            setEstadoSwitch={() => {
              setConfirmDialog({
                isOpen: true,
                title: "Cambiar estado",
                subtitle: "¿Seguro que desea cambiar el estado del tipo de riesgo?",
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  patchEstado.mutate({
                    id: row.original.tipoRiesgoId!,
                    estado: !row.original.estado,
                  });
                },
              });
            }}
          />
        ),
      },
    ],
    []
  );

  if (!isSuper || !tieneAcceso) {
    return (
      <SingleTableBoxScene
        title={"Tipos de riesgo de control de cambios"}
        urlRedirect={`${returnUrl}/crear`}
        py={2}
      >
        <GeneralTable<TipoRiesgo>
          columns={columns}
          data={response?.data || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          enableGlobalFilter={false}
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          pagination={pagination}
          onPaging={setPagination}
          rowCount={response?.totalElements ?? 0}
          actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
          positionActionsColumn="first"
          canEdit={true}
          onEdit={onEdit}
          canDelete={true}
          onDelete={onDelete}
        />
      </SingleTableBoxScene>
    );
  }
  return <Outlet />;
}
